/* File: src/components/game/GameCountdown.js */
import React, { useState, useEffect, useRef } from 'react';
import { playCountdownSound } from '../../utils/countdownSounds'; // Sounds utility
import { formatCombinedDifficulty } from '../../contexts/gameHelpers'; // Formatting helper
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faLightbulb } from '@fortawesome/free-solid-svg-icons'; // Icons

// --- Import Constants and Helpers for Coaching Tip ---
import { COACHING_MESSAGES } from '../../constants/gameConstants'; // Import the messages object
import { getTierFromDifficulty } from '../../contexts/gameHelpers'; // Import the tier helper function
// --- End Import ---

/**
 * GameCountdown Component
 * Displays a 3-2-1 countdown before the game starts, along with the level
 * and a relevant coaching tip. Calls onComplete when finished, onCancel if cancelled.
 */
const GameCountdown = ({ onComplete, onCancel, nBack, difficulty, isChallenge }) => {
    // --- State ---
    const [countdown, setCountdown] = useState(3); // Countdown number (3, 2, 1)
    const [pulsing, setPulsing] = useState(false); // State for pulse animation
    const [coachingTip, setCoachingTip] = useState(''); // State to hold the selected tip

    // --- Refs ---
    const completedRef = useRef(false); // Prevents calling onComplete multiple times
    const prevCountdownRef = useRef(null); // Tracks previous countdown value for sound triggering

    // --- ADD LOGGING HERE ---
    useEffect(() => {
      console.log(`[GameCountdown Render/Props Update] Received props: nBack=${nBack}, difficulty=${difficulty}, isChallenge=${isChallenge}`);
  }, [nBack, difficulty, isChallenge]); // Log whenever these props change
  // --- END LOGGING ---

    // --- Effect: Play Sound and Trigger Pulse ---
    // Runs when the countdown value changes.
    useEffect(() => {
        // Only play sound and pulse for numbers 3, 2, 1
        if (countdown > 0 && prevCountdownRef.current !== countdown) {
            playCountdownSound(countdown); // Play the corresponding sound
            prevCountdownRef.current = countdown; // Update previous value tracker

            // Trigger pulse animation
            setPulsing(true);
            const pulseTimer = setTimeout(() => {
                // Check if component is still mounted before setting state
                if (completedRef.current === false) { // Avoid setting state after completion starts
                     setPulsing(false);
                }
            }, 300); // Duration matches CSS animation typically

            // Cleanup timer on unmount or before next run
            return () => clearTimeout(pulseTimer);
        }
    }, [countdown]); // Dependency: countdown

    // --- Effect: Manage Countdown Timer ---
    // Decrements the countdown or calls onComplete.
    useEffect(() => {
        // If countdown is still running (> 0)
        if (countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(c => c - 1); // Decrement countdown after 1 second
            }, 1000);
            // Cleanup timer if effect re-runs or component unmounts
            return () => clearTimeout(timer);
        }
        // If countdown just reached 0 and we haven't already called onComplete
        else if (countdown === 0 && !completedRef.current) {
            completedRef.current = true; // Mark as completed to prevent multiple calls
            console.log("GameCountdown: Countdown reached zero - calling onComplete IMMEDIATELY.");
            onComplete(); // Call the completion handler passed via props
            // No timeout delay needed - transition handled by parent component
        }
    }, [countdown, onComplete]); // Dependencies: countdown, onComplete callback

    // --- Effect: Select Coaching Tip ---
    // Selects and formats a coaching tip based on nBack and difficulty.
    useEffect(() => {
        console.log(`GameCountdown Tip Effect: nBack=${nBack}, difficulty=${difficulty}`);
        try {
            // 1. Determine the tier based on difficulty
            const tier = getTierFromDifficulty(difficulty); // Use helper (expects 0.00-0.95 range or similar)
            const tierName = tier.name.toUpperCase(); // Match keys in COACHING_MESSAGES (e.g., 'NOVICE')
            console.log(`GameCountdown Tip Effect: Determined Tier=${tierName}`);

            // 2. Get messages array for that tier
            const messages = COACHING_MESSAGES[tierName];

            if (messages && messages.length > 0) {
                // 3. Select a random message from the array
                let selectedMessage = messages[Math.floor(Math.random() * messages.length)];
                // 4. Replace placeholder {n} with actual nBack value
                selectedMessage = selectedMessage.replace('{n}', nBack);
                setCoachingTip(selectedMessage); // Update state with the formatted tip
                console.log(`GameCountdown Tip Effect: Selected Tip='${selectedMessage}'`);
            } else {
                console.warn(`GameCountdown Tip Effect: No coaching messages found for tier: ${tierName}`);
                setCoachingTip(''); // Clear tip if none are available for the tier
            }
        } catch (error) {
             console.error("GameCountdown Tip Effect: Error selecting coaching tip:", error);
             setCoachingTip(''); // Clear tip on any error during selection
        }
    // Rerun this effect if nBack or difficulty changes during countdown (unlikely but safe)
    }, [nBack, difficulty]);

    // --- Rendering Logic ---

    // Determine the color for the countdown number dynamically
    let numberColor = "text-indigo-500"; // Default color
    if (countdown === 1) numberColor = "text-amber-500"; // Color for "1"

    // Format the level display string (e.g., "2.35" or "Challenge 3.00")
    console.log(`[GameCountdown Formatting] Calling formatCombinedDifficulty with: nBack=${nBack}, difficulty=${difficulty}`);
    const displayLevelValue = formatCombinedDifficulty(nBack, difficulty);
    console.log(`[GameCountdown Formatting] Result from formatCombinedDifficulty: ${displayLevelValue}`);
    const levelLabel = isChallenge ? 'Challenge Level:' : 'Level:';

    // *** Do not render anything if countdown is zero or less ***
    // The parent component (GameScreen) handles the transition away from countdown.
    if (countdown <= 0) {
        return null; // Render nothing once countdown hits zero
    }

    // --- Render the Countdown UI (for 3, 2, 1) ---
    return (
        // Full overlay container
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-10 animate-[fadeIn_0.3s_ease-in]">
            {/* Centered modal box */}
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4 border border-gray-200">
                {/* Main countdown area */}
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-3 text-gray-800">Get Ready!</h2>
                    {/* Number display with pulse animation */}
                    <div
                        className={`transition-transform duration-300 mb-6`}
                        style={{ transform: pulsing ? 'scale(1.15)' : 'scale(1)' }}
                    >
                        <span className={`text-8xl font-bold ${numberColor}`}>
                            {countdown}
                        </span>
                    </div>
                    {/* Level display */}
                    <div className="text-sm text-gray-600 mb-5 px-4 py-2 rounded-full bg-gray-50 inline-block border border-gray-200">
                        {levelLabel} <span className="font-semibold">{displayLevelValue}</span>
                    </div>
                </div>

                {/* Coaching Tip section (only renders if a tip exists) */}
                {coachingTip && (
                    <div className="mt-5 mb-5">
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-blue-700 text-left rounded-r-md shadow-sm">
                            <div className="flex items-start">
                                <FontAwesomeIcon icon={faLightbulb} className="text-blue-500 mr-2 mt-0.5 flex-shrink-0 h-4 w-4" />
                                <div className="text-sm">{coachingTip}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Cancel Button section */}
                <div className="mt-5 pt-4 border-t border-gray-200 text-center">
                    <button
                        onClick={onCancel} // Calls the cancel handler passed from GameScreen
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors duration-150 flex items-center justify-center mx-auto border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                        title="Cancel game start and return"
                    >
                        <FontAwesomeIcon icon={faTimes} className="mr-1.5 h-3.5 w-3.5" />
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GameCountdown;